import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { payWithRazorpay } from "@/lib/payments";
import { useMembership } from "@/lib/membership";
import { localDate } from "@/lib/focus";
import { browserTz, fmtSessionTime, money, onIndiaTime, sessionStart, tzCity } from "@/lib/locale";

function todayISO() { return localDate(); }

export default function Booking() {
  const { user } = useAuth();
  const { push } = useToast();
  const navigate = useNavigate();
  const [trainers, setTrainers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [trainerId, setTrainerId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [slots, setSlots] = useState([]); // [{ key, date, time, label }] — date/time in India time, label in theirs
  const [time, setTime] = useState("");     // the chosen slot's key
  const [saving, setSaving] = useState(false);
  const [pay, setPay] = useState({ enabled: false, price: null });
  const local = !onIndiaTime();
  const [membership, reloadMembership] = useMembership();
  const [payingId, setPayingId] = useState(null);

  const load = useCallback(() => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/trainers").then((r) => {
      setTrainers(r.data.trainers);
      setTrainerId(r.data.trainers[0]?.trainer_id || "");
    }).catch(() => {});
    api.get("/payments/config").then((r) => setPay({ enabled: r.data.enabled, price: r.data.session_price || { amount: r.data.session_price_inr, currency: "INR" } })).catch(() => {});
    load();
  }, [load]);

  useEffect(() => {
    if (!trainerId || !date) { setSlots([]); return; }
    setTime("");
    api.get(`/trainers/${trainerId}/slots`, { params: local ? { date, tz: browserTz() } : { date } })
      .then((r) => setSlots(r.data.options
        ? r.data.options.map((o) => ({ key: `${o.date} ${o.time}`, date: o.date, time: o.time, label: o.label }))
        : r.data.slots.map((t) => ({ key: `${date} ${t}`, date, time: t, label: t }))))
      .catch(() => setSlots([]));
  }, [trainerId, date, bookings, local]);

  const book = async () => {
    const slot = slots.find((x) => x.key === time);
    if (!trainerId || !slot) { push("Pick a trainer, date and time.", "error"); return; }
    setSaving(true);
    try {
      const { data: made } = await api.post("/bookings", { trainer_id: trainerId, date: slot.date, time: slot.time });
      push(made.status === "requested" ? "Intro session requested — your coach will confirm it shortly." : "Session booked.", "success");
      reloadMembership();
      setTime("");
      load();
    } catch (e) {
      push(e?.response?.data?.detail || "Could not book session.", "error");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => { await api.delete(`/bookings/${id}`).catch(() => {}); push("Booking cancelled."); load(); };

  const paySession = (b) => {
    setPayingId(b.id);
    payWithRazorpay({
      orderPayload: { type: "session", booking_id: b.id },
      user,
      onSuccess: () => { setPayingId(null); push("Session payment successful.", "success"); load(); },
      onError: (e) => { setPayingId(null); push(e?.response?.data?.detail || e.message || "Payment failed.", "error"); },
    });
  };

  const selectedTrainer = trainers.find((t) => t.trainer_id === trainerId);

  return (
    <div>
      <PageHeader eyebrow="Schedule" title="Book a Session" subtitle="Reserve a live video session with your coach."
        action={<button className="btn btn-ghost" onClick={() => navigate("/call-check")} data-testid="open-call-check"><Icons.MonitorCheck size={16} /> Test camera &amp; mic</button>} />

      {membership && (
        <div className={`clay-inset row-wrap fade-up`} style={{ padding: "12px 14px", marginBottom: 16, gap: 10 }} data-testid="booking-balance">
          <Icons.Ticket size={17} color="var(--gold)" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 13.5 }}>
            {membership.unlimited_sessions ? "Your membership includes unlimited sessions."
              : membership.credits > 0 ? `${membership.credits} session credit${membership.credits === 1 ? "" : "s"} — your next booking is covered.`
              : membership.has_access ? `No session credits left — new bookings are ${pay.price ? money(pay.price) : "…"} each, or get a pack.`
              : "Your membership has ended. Get a session pack or renew to book."}
          </span>
          {!membership.unlimited_sessions && membership.credits === 0 && <button className="btn btn-ghost" onClick={() => navigate("/membership")} style={{ padding: "8px 12px", fontSize: 13 }}>Get sessions</button>}
        </div>
      )}

      <div className="grid-2">
        <div className="clay fade-up min0" style={{ padding: 22 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>{trainers.length > 1 ? "Choose your coach" : "Your coach"}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
            {trainers.map((t) => (
              <button key={t.trainer_id} data-testid={`trainer-${t.trainer_id}`} onClick={() => setTrainerId(t.trainer_id)}
                className="clay-inset" style={{ border: trainerId === t.trainer_id ? "1px solid var(--ink)" : "1px solid transparent", cursor: "pointer", padding: "12px 14px", display: "flex", alignItems: "center", gap: 13, textAlign: "left" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text-2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600 }}>{t.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>{t.specialty}</div>
                </div>
                {trainerId === t.trainer_id && <Icons.Check size={18} color="var(--text)" />}
              </button>
            ))}
            {trainers.length === 0 && <div className="clay-inset" style={{ fontSize: 13.5, color: "var(--text-2)", padding: 16 }}>You can book sessions once your coach has been assigned.</div>}
          </div>

          <label className="label">Date</label>
          <input type="date" className="field" data-testid="booking-date" value={date} min={todayISO()} onChange={(e) => setDate(e.target.value)} style={{ marginBottom: 18 }} />

          <label className="label">Available slots {selectedTrainer ? `with ${selectedTrainer.name}` : ""}{local ? ` · your time (${tzCity()})` : ""}</label>
          {slots.length === 0 ? (
            <div className="clay-inset" style={{ padding: "16px", textAlign: "center", fontSize: 13, color: "var(--text-3)" }}>No open slots on this day. Try another date.</div>
          ) : (
            <div className="grid-slots">
              {slots.map((s) => (
                <button key={s.key} data-testid={`slot-${s.time}`} onClick={() => setTime(s.key)}
                  className={time === s.key ? "" : "clay-inset"} title={local ? `${s.time} in India` : undefined}
                  style={{ padding: "11px 0", borderRadius: 12, border: time === s.key ? "1px solid var(--ink)" : "1px solid transparent", cursor: "pointer",
                    background: time === s.key ? "var(--ink)" : undefined, color: time === s.key ? "#fff" : "var(--text)", fontWeight: 500, fontSize: 13.5 }}>
                  {s.label}
                </button>
              ))}
            </div>
          )}

          <button data-testid="confirm-booking-btn" className="btn btn-primary" disabled={saving || !time} onClick={book} style={{ width: "100%", marginTop: 22, padding: 14 }}>
            <Icons.CalendarCheck size={18} /> {saving ? "Booking..." : "Confirm booking"}
          </button>
        </div>

        <div className="clay fade-up min0" style={{ padding: 22, animationDelay: "80ms" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <div className="eyebrow">Upcoming sessions</div>
            <span className="chip chip-neutral">{bookings.length} booked</span>
          </div>
          {bookings.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px 0", color: "var(--text-3)" }}>
              <Icons.CalendarX size={34} style={{ marginBottom: 10, opacity: 0.6 }} />
              <div style={{ fontSize: 14 }}>No sessions booked yet.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }} data-testid="bookings-list">
              {bookings.map((b) => (
                <div key={b.id} className="clay-inset" style={{ padding: "15px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--teal-soft)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--teal)" }}>
                    <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1 }}>{(local ? sessionStart(b) : new Date(b.date + "T12:00")).getDate()}</span>
                    <span style={{ fontSize: 10, fontWeight: 600 }}>{(local ? sessionStart(b) : new Date(b.date + "T12:00")).toLocaleDateString("en-US", { month: "short" })}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{b.trainer_name} · {fmtSessionTime(b)}</div>
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>{b.specialty}</div>
                  </div>
                  {b.status === "requested" ? (
                    <span className="chip chip-amber" data-testid={`requested-${b.id}`}><Icons.Hourglass size={12} /> Waiting for coach to confirm</span>
                  ) : (
                    <button data-testid={`join-${b.id}`} className="btn btn-ghost" onClick={() => navigate(`/call/${b.id}`)} style={{ padding: "8px 13px", fontSize: 12.5, color: "var(--teal)" }}>
                      <Icons.Video size={15} /> Join
                    </button>
                  )}
                  {b.paid ? (
                    <span className="chip chip-teal" data-testid={`paid-${b.id}`}><Icons.Check size={13} /> Paid</span>
                  ) : pay.enabled ? (
                    <button data-testid={`pay-${b.id}`} className="btn btn-primary" disabled={payingId === b.id} onClick={() => paySession(b)} style={{ padding: "8px 13px", fontSize: 12.5 }}>
                      {payingId === b.id ? "..." : `Pay ${money(pay.price)}`}
                    </button>
                  ) : (
                    <span className="chip chip-neutral">Unpaid</span>
                  )}
                  <Icons.Trash2 size={17} data-testid={`cancel-${b.id}`} style={{ cursor: "pointer", color: "var(--text-3)" }} onClick={() => cancel(b.id)} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
